/**
 * Theme context providing theme mode state, toggle function,
 * workspace preset loading, CVD palette selection, reduced motion,
 * font size, font family, and localStorage persistence.
 *
 * @module theme/ThemeContext
 * @see TASK-FRONTEND-THEME-PROVIDER-CHG-003
 * @see SPEC-FRONTEND-RULE-007
 * @see SPEC-FRONTEND-RULE-008
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { WorkspaceThemePreset } from "./themes";
import type { CvdVariant } from "./cvdPalettes";
import { DARK_MODE_ENABLED } from "./buildFlags";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ThemeMode = "light" | "dark" | "system";

export interface ThemeState {
  /** Current theme mode selection. */
  mode: ThemeMode;
  /** Resolved effective mode after system detection. */
  effectiveMode: "light" | "dark";
  /** Currently loaded workspace preset (null = default). */
  workspacePreset: WorkspaceThemePreset | null;
  /** Active CVD palette variant (null = none). */
  cvdVariant: CvdVariant | null;
  /** Whether reduced motion is enabled. */
  reducedMotion: boolean;
  /** Font size in pixels. */
  fontSize: number;
  /** Font family string. */
  fontFamily: string;
}

export interface ThemeActions {
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  setWorkspacePreset: (preset: WorkspaceThemePreset | null) => void;
  setCvdVariant: (variant: CvdVariant | null) => void;
  setReducedMotion: (enabled: boolean) => void;
  setFontSize: (size: number) => void;
  setFontFamily: (family: string) => void;
}

export type ThemeContextValue = ThemeState & ThemeActions;

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

const STORAGE_KEY_MODE = "verisa-theme-mode";
const STORAGE_KEY_CVD = "verisa-cvd-variant";
const STORAGE_KEY_REDUCED_MOTION = "verisa-reduced-motion";
const STORAGE_KEY_FONT_SIZE = "verisa-font-size";
const STORAGE_KEY_FONT_FAMILY = "verisa-font-family";

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------

const DEFAULT_FONT_SIZE = 14;
const DEFAULT_FONT_FAMILY =
  '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeStorage(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or unavailable — silently ignore.
  }
}

function getSystemPreference(): "light" | "dark" {
  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface ThemeContextProviderProps {
  children: React.ReactNode;
  defaultMode?: ThemeMode;
}

export function ThemeContextProvider({
  children,
  defaultMode = "system",
}: ThemeContextProviderProps): React.ReactElement {
  const [mode, setModeRaw] = useState<ThemeMode>(
    () => readStorage<ThemeMode>(STORAGE_KEY_MODE, defaultMode),
  );
  const [systemPref, setSystemPref] = useState<"light" | "dark">(
    getSystemPreference,
  );
  const [workspacePreset, setWorkspacePresetRaw] =
    useState<WorkspaceThemePreset | null>(null);
  const [cvdVariant, setCvdVariantRaw] = useState<CvdVariant | null>(
    () => readStorage<CvdVariant | null>(STORAGE_KEY_CVD, null),
  );
  const [reducedMotion, setReducedMotionRaw] = useState<boolean>(
    () => readStorage<boolean>(STORAGE_KEY_REDUCED_MOTION, false),
  );
  const [fontSize, setFontSizeRaw] = useState<number>(
    () => readStorage<number>(STORAGE_KEY_FONT_SIZE, DEFAULT_FONT_SIZE),
  );
  const [fontFamily, setFontFamilyRaw] = useState<string>(
    () => readStorage<string>(STORAGE_KEY_FONT_FAMILY, DEFAULT_FONT_FAMILY),
  );

  // Detect system color scheme changes
  useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      setSystemPref(e.matches ? "dark" : "light");
    };
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  // The single choke point for mode resolution, gated by the BUILD-TIME flag
  // (`D-DM-07`(a)). When dark mode is not built in, every SPA renders light
  // regardless of the stored preference or the OS setting -- including Admin,
  // which already mounts the provider with no `defaultMode` and so already
  // follows prefers-color-scheme today, over ~19 files that still read the
  // frozen light palette. Gating HERE rather than per-SPA is what makes the
  // flag's OFF state fix that too.
  //
  // The stored preference itself is deliberately left untouched: turning the
  // flag on later restores the user's choice instead of silently resetting it.
  const effectiveMode: "light" | "dark" = !DARK_MODE_ENABLED
    ? "light"
    : mode === "system"
      ? systemPref
      : mode;

  // --- Persisted setters ---------------------------------------------------

  const setMode = useCallback((m: ThemeMode) => {
    setModeRaw(m);
    writeStorage(STORAGE_KEY_MODE, m);
  }, []);

  const toggleMode = useCallback(() => {
    setModeRaw((prev) => {
      const next: ThemeMode =
        prev === "light" ? "dark" : prev === "dark" ? "system" : "light";
      writeStorage(STORAGE_KEY_MODE, next);
      return next;
    });
  }, []);

  const setWorkspacePreset = useCallback(
    (preset: WorkspaceThemePreset | null) => {
      setWorkspacePresetRaw(preset);
    },
    [],
  );

  const setCvdVariant = useCallback((variant: CvdVariant | null) => {
    setCvdVariantRaw(variant);
    writeStorage(STORAGE_KEY_CVD, variant);
  }, []);

  const setReducedMotion = useCallback((enabled: boolean) => {
    setReducedMotionRaw(enabled);
    writeStorage(STORAGE_KEY_REDUCED_MOTION, enabled);
  }, []);

  const setFontSize = useCallback((size: number) => {
    const clamped = Math.min(24, Math.max(12, size));
    setFontSizeRaw(clamped);
    writeStorage(STORAGE_KEY_FONT_SIZE, clamped);
  }, []);

  const setFontFamily = useCallback((family: string) => {
    setFontFamilyRaw(family);
    writeStorage(STORAGE_KEY_FONT_FAMILY, family);
  }, []);

  // --- Context value -------------------------------------------------------

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      effectiveMode,
      workspacePreset,
      cvdVariant,
      reducedMotion,
      fontSize,
      fontFamily,
      setMode,
      toggleMode,
      setWorkspacePreset,
      setCvdVariant,
      setReducedMotion,
      setFontSize,
      setFontFamily,
    }),
    [
      mode,
      effectiveMode,
      workspacePreset,
      cvdVariant,
      reducedMotion,
      fontSize,
      fontFamily,
      setMode,
      toggleMode,
      setWorkspacePreset,
      setCvdVariant,
      setReducedMotion,
      setFontSize,
      setFontFamily,
    ],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the Verisa theme context (mode, CVD, reduced motion, etc.).
 * Must be used within a ThemeContextProvider.
 */
export function useVerisaTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error(
      "useVerisaTheme must be used within a ThemeContextProvider",
    );
  }
  return ctx;
}

export { ThemeContext };
